import { useState } from 'react'
import {
  Activity,
  Bell,
  ChevronRight,
  ClipboardList,
  FileText,
  FlaskConical,
  Smartphone,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { formatRelativeTime } from '@/lib/relativeTime'
import { typeStyle } from '@/lib/notificationStyles'
import { notificationLink } from '@/lib/notificationLink'
import {
  NOTIFICATION_CATEGORY_GROUPS,
  categoryGroupFromParam,
  groupOf,
  groupUnreadCount,
  type NotificationCategoryGroup,
} from '@/lib/notificationCategories'
import {
  useMarkAllRead,
  useMarkRead,
  useNotificationPages,
  useUnreadCount,
} from '@/hooks/useNotifications'
import { PageHeading } from '@/components/layout/PageHeading'
import { LisReviewSettingsButton } from '@/components/lis-review/LisReviewSettings'
import { PERMISSIONS, usePermission } from '@/lib/permissions'

const FILTERS = [
  { label: 'All', unreadOnly: false },
  { label: 'Unread only', unreadOnly: true },
]

/** The page URL's category param, so a link or bookmark opens one category. */
const CATEGORY_PARAM = 'category'

const GROUP_ICONS: Record<string, LucideIcon> = {
  patients: Users,
  procedures: ClipboardList,
  lis: FlaskConical,
  instruments: Activity,
  forms: FileText,
  offline: Smartphone,
}

export function NotificationsPage() {
  const [params, setParams] = useSearchParams()
  const group = categoryGroupFromParam(params.get(CATEGORY_PARAM))
  const [unreadOnly, setUnreadOnly] = useState(false)
  const { can } = usePermission()
  const { data: unread } = useUnreadCount()
  const unreadCount = unread?.count ?? 0
  const groupUnread = groupUnreadCount(group, unread)

  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useNotificationPages({ unreadOnly, categories: group?.categories })
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()
  const items = data?.pages.flatMap((p) => p.items) ?? []

  function selectGroup(next: NotificationCategoryGroup | undefined) {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev)
        if (next) out.set(CATEGORY_PARAM, next.id)
        else out.delete(CATEGORY_PARAM)
        return out
      },
      { replace: true },
    )
  }

  const chips: { key: string; label: string; icon: LucideIcon; group: NotificationCategoryGroup | undefined }[] = [
    { key: 'all', label: 'All', icon: Bell, group: undefined },
    ...NOTIFICATION_CATEGORY_GROUPS.map((g) => ({ key: g.id, label: g.label, icon: GROUP_ICONS[g.id] ?? Bell, group: g })),
  ]

  const where = group ? ` in ${group.label}` : ''

  return (
    <div className='p-6'>
      <div className='mb-6 flex items-center gap-3'>
        <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-red-50'>
          <Bell className='h-5 w-5 text-red-500' />
        </div>
        <PageHeading
          className='flex-1'
          title='Notifications'
          description={`${unreadCount} unread notifications`}
        />
        {can(PERMISSIONS.integrationsManage) && <LisReviewSettingsButton />}
        {groupUnread > 0 && (
          <button
            onClick={() => markAllRead.mutate(group?.categories)}
            disabled={markAllRead.isPending}
            className='rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'
          >
            Mark all as read{where}
          </button>
        )}
      </div>

      <div className='mb-4 space-y-2'>
        <span
          id='notifications-category'
          className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
        >
          Category
        </span>
        {/* Same chips as the Audit Trail Center: one category at a time, its
            unread count beside the label. */}
        <div className='flex flex-wrap gap-2' role='radiogroup' aria-labelledby='notifications-category'>
          {chips.map(({ key, label, icon: Icon, group: g }) => {
            const on = (group?.id ?? 'all') === key
            const n = groupUnreadCount(g, unread)
            return (
              <button
                key={key}
                type='button'
                role='radio'
                aria-checked={on}
                onClick={() => selectGroup(g)}
                className={cn(
                  'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  on
                    ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                    : 'bg-primary/10 text-primary hover:bg-primary/15',
                )}
              >
                <Icon className='h-3.5 w-3.5' aria-hidden />
                {label}
                <span
                  className={cn(
                    'tabular-nums',
                    on ? 'text-primary-foreground/80' : 'text-primary/70',
                    n === 0 && 'opacity-50',
                  )}
                >
                  {n.toLocaleString()}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <div className='mb-4 inline-flex rounded-lg border border-gray-200 p-0.5'>
        {FILTERS.map((f) => (
          <button
            key={f.label}
            onClick={() => setUnreadOnly(f.unreadOnly)}
            className={cn(
              'rounded-md px-3 py-1 text-sm font-medium transition-colors',
              unreadOnly === f.unreadOnly
                ? 'bg-gray-900 text-white'
                : 'text-gray-600 hover:text-gray-900',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className='py-10 text-center text-sm text-gray-400'>Loading notifications…</p>
      ) : isError && !data ? (
        <div className='py-10 text-center'>
          <p className='text-sm text-gray-500'>Couldn’t load notifications.</p>
          <button
            onClick={() => void refetch()}
            className='mt-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50'
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className='py-10 text-center text-sm text-gray-400'>
          {unreadOnly ? `No unread notifications${where}.` : group ? `No notifications${where} yet.` : 'No notifications yet.'}
        </p>
      ) : (
        <div className='space-y-2'>
          {items.map((n) => {
            const s = typeStyle(n.type)
            const isUnread = n.readAt === null
            const link = notificationLink(n)
            // In All, each row names its category; inside one category that
            // would only repeat the selected chip.
            const rowGroup = group ? undefined : groupOf(n)
            // The message area is the link; "Mark as read" stays a separate
            // action so a notification can be dismissed without navigating.
            const detail = (
              <>
                <p className='text-sm text-gray-700'>{n.message}</p>
                <p className='mt-0.5 text-xs text-gray-400'>
                  {rowGroup && `${rowGroup.label} · `}
                  {formatRelativeTime(n.createdAt)}
                </p>
              </>
            )
            return (
              <div
                key={n.id}
                className={cn(
                  'flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm',
                  !isUnread && 'opacity-60',
                  link && 'transition-colors hover:border-gray-300 hover:bg-gray-50',
                )}
              >
                <span
                  className={cn(
                    'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
                    isUnread ? s.dot : 'bg-gray-300',
                  )}
                />
                <div className='flex-1'>
                  {link ? (
                    <Link
                      to={link}
                      onClick={() => isUnread && markRead.mutate(n.id)}
                      className='group block'
                    >
                      {detail}
                    </Link>
                  ) : (
                    detail
                  )}
                </div>
                {isUnread && (
                  <button
                    onClick={() => markRead.mutate(n.id)}
                    disabled={markRead.isPending}
                    className='shrink-0 text-xs font-medium text-blue-600 hover:underline disabled:opacity-50'
                  >
                    Mark as read
                  </button>
                )}
                {link && (
                  <Link to={link} aria-label='Open' className='shrink-0 text-gray-300 hover:text-gray-500'>
                    <ChevronRight className='h-4 w-4' />
                  </Link>
                )}
              </div>
            )
          })}
          {hasNextPage && (
            <div className='pt-2 text-center'>
              {isFetchNextPageError && (
                <p className='mb-2 text-sm text-gray-500'>Couldn’t load more notifications.</p>
              )}
              <button
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                className='rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
